import styled from "styled-components";
import dayjs from "dayjs";
import { DatePicker as AntDatePicker } from "antd";
import { useSelector } from "react-redux";
import { noop, range } from "lodash";
const timezone = require("dayjs/plugin/timezone");

import { RATypography } from "../Typography";
import { isFormatTime12H } from "@/stores/slices/authSlice";

const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);
dayjs.extend(timezone);

const DATE_PICKER_FORMAT = "dddd, MMM DD";
const DATETIME_PICKER_FORMAT = "dddd, MMM DD HH:mm";
const DATETIME_PICKER_FORMAT_12H = "dddd, MMM DD hh:mm A";

export const RADatePicker = ({
	title,
	value = null,
	picker,
	onChange = noop,
	onSelect = noop,
	width = "100%",
	height = "52px",
	prefix,
	error,
	placeholder,
	showTime = false,
	disabledFromDate,
	disabled = false,
	format,
	defaultPickerValue = null,
	"data-testid": dataTestId = null,
}) => {
	const isTime12H = useSelector(isFormatTime12H);
	const disabledDate = (current) => {
		// Can not select days before today and today
		if (disabledFromDate) {
			return current && current < dayjs(disabledFromDate).add(-1, "days").endOf("day");
		}
		return current && current < dayjs().add(-1, "days").endOf("day");
	};
	// On the boundary day itself, the times before disabledFromDate must go too.
	const disabledTime = (current) => {
		if (!showTime || !disabledFromDate || !current) {
			return {};
		}
		const min = dayjs(disabledFromDate);
		if (!current.isSame(min, "day")) {
			return {};
		}
		return {
			disabledHours: () => range(0, min.hour()),
			disabledMinutes: (selectedHour) => (selectedHour === min.hour() ? range(0, min.minute()) : []),
		};
	};

	const customFormat = () => {
		if (format) return format;
		return showTime ? (isTime12H ? DATETIME_PICKER_FORMAT_12H : DATETIME_PICKER_FORMAT) : DATE_PICKER_FORMAT;
	};

	return (
		<WrapperDatePicker>
			{title && (
				<WrapperTitle>
					<RATypography color="#595959" variant="sub-heading4">
						{title}
					</RATypography>
				</WrapperTitle>
			)}
			<StyledDatePicker
				value={value ? dayjs(value) : null}
				onChange={(e) => onChange(e.toISOString())}
				onSelect={(e) => onSelect(e.toISOString())}
				format={customFormat()}
				placeholder={placeholder}
				inputReadOnly
				showToday={false}
				allowClear={false}
				showNow={false}
				disabled={disabled}
				placement="bottomLeft"
				suffixIcon={prefix}
				picker={picker}
				disabledDate={disabledDate}
				disabledTime={disabledTime}
				style={{ width: width, height: height }}
				showTime={
					showTime
						? {
								format: isTime12H ? "hh:mm A" : "HH:mm",
								minuteStep: 5,
							}
						: false
				}
				superNextIcon={null}
				superPrevIcon={null}
				mode="date"
				size="large"
				panelRender={(originPanel) => <PanelWrapper>{originPanel}</PanelWrapper>}
				defaultPickerValue={defaultPickerValue}
				data-testid={dataTestId}
			/>
			{!!error && (
				<RATypography variant="body3" color="#BF1A2F" top="4px">
					{error}
				</RATypography>
			)}
		</WrapperDatePicker>
	);
};

const StyledDatePicker = styled(AntDatePicker)`
	width: 100%;
	border-radius: 12px;
	border: 1px solid #e9e8e8;
	&.ant-picker-large {
		height: 52px;
		padding-left: 20px;
		padding-right: 20px;
		@media (max-width: 576px) {
			padding-left: 10px;
			padding-right: 10px;
		}
	}
	.ant-picker-header {
		padding: 12px 24px !important;
	}
`;
const WrapperDatePicker = styled.div`
	position: relative;
	.ant-picker {
		&:hover {
			border-color: #ffb11a !important;
		}
	}
	.ant-picker-focused {
		border-color: #ffb11a !important;
	}
	.ant-picker .ant-picker-input {
		gap: 6px;
		@media (max-width: 576px) {
			gap: 0px;
		}
	}
	.ant-picker .ant-picker-input span.ant-picker-suffix,
	.ant-picker.ant-picker-range span.ant-picker-suffix {
		margin-left: 1px;
		margin-right: 6px;
		order: -1;
	}
	.ant-picker-large .ant-picker-input > input {
		font-size: 14px;
		letter-spacing: 0.5px;
	}
`;
const WrapperTitle = styled.div`
	padding-bottom: 8px;
`;
const PanelWrapper = styled.div`
	.ant-picker-date-panel {
		width: auto;
	}
	.ant-picker-header {
		padding: 12px 24px;
	}
	.ant-picker-header-super-prev-btn,
	.ant-picker-header-super-next-btn {
		display: none;
	}
	.ant-picker-header-prev-btn,
	.ant-picker-header-next-btn {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.ant-picker-header-view {
		font-size: 16px;
		line-height: 24px;
		color: #222222;
		font-weight: 500;
	}
	.ant-picker-content thead th {
		font-size: 12px;
		line-height: 20px;
		color: #e9e8e8;
		font-weight: 700;
		text-transform: uppercase;
	}
	.ant-picker-date-panel .ant-picker-content th {
		width: 40px;
	}
	.ant-picker-cell .ant-picker-cell-inner {
		display: flex;
		min-width: 40px;
		height: 40px;
		line-height: 40px;
		border-radius: 40px;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		margin: 0 auto;
	}
	.ant-picker-cell-today .ant-picker-cell-inner::before {
		border-radius: 40px !important;
	}
	.ant-picker-cell {
		padding: 2px 0;
	}
	.ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
		width: 100%;
		height: 36px;
		color: #222222;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		padding-inline-start: 0;
	}
	.ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
		background: #ffb11a;
	}
	.ant-picker-time-panel-column {
		width: 75px;
	}
	.ant-picker-ranges .ant-picker-ok {
		margin-inline-start: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
	}
	.ant-picker-ranges .ant-picker-ok button {
		height: 40px;
		padding: 0px 40px;
		border-radius: 32px;
		background-color: transparent;
		border: 1px solid #e9e8e8;
		color: #222222;
		font-size: 14px;
	}
	.ant-picker-time-panel-column::after {
		height: 0 !important;
	}
	.ant-picker-time-panel-column::-webkit-scrollbar {
		width: 4px;
	}
	.ant-picker-time-panel-column::-webkit-scrollbar-thumb {
		border-radius: 8px;
		background: #ededed;
	}
`;
